import { ActivitySubjects } from "@outschool/business-rules";
import { Activity, DeliveryType } from "@outschool/gql-backend-generated";
import { TFunction, useTranslation } from "@outschool/localization";
import { participationGuidelinesUrl } from "@outschool/routes";
import { useActivityString as sharedUseActivityString } from "@outschool/ui-components-website";

import {
  ACTIVITY_DELIVERY,
  ACTIVITY_FORMAT,
  ACTIVITY_STATUS,
  ActivityFormatType,
  format,
  isArchived,
  isPublished,
  requestedListingAt,
} from "../../Activity";
import exhaustiveCheck from "../../exhaustiveCheck";

function getTranslatedDeliveryType(
  deliveryType: DeliveryType,
  t: TFunction,
): string {
  switch (deliveryType) {
    case DeliveryType.OneOnOneClass:
      return t("One-on-one class");
    case DeliveryType.OneTimeClass:
      return t("One-time class");
    case DeliveryType.OngoingClass:
      return t("Ongoing class");
    case DeliveryType.SemesterCourse:
      return t("Semester course");
    case DeliveryType.ShortCourse:
      return t("Short course");
    case DeliveryType.Camp:
      return t("Camp");
    case DeliveryType.Club:
      return t("Chat group");
    default:
      throw exhaustiveCheck(deliveryType);
  }
}

function translateActivityClassFormat(
  activity: ActivityFormatType,
  t: TFunction,
): string {
  const activityFormat = format(activity);

  switch (activityFormat) {
    case ACTIVITY_FORMAT.FLEX:
      return t("Flexible Schedule Course");
    case ACTIVITY_FORMAT.ONGOING:
      return t("Ongoing Course");
    case ACTIVITY_FORMAT.ONE_TIME:
      return t("One-Time Class");
    case ACTIVITY_FORMAT.ONE_ON_ONE:
      return t("1-on-1 Class");
    case ACTIVITY_FORMAT.MULTI_DAY:
      return t("Multi-Day Course");
    case ACTIVITY_FORMAT.CLUB:
      return t("Group");
    default:
      throw exhaustiveCheck(activityFormat);
  }
}

function translateActivityFormat(
  format: ACTIVITY_FORMAT,
  t: TFunction,
): string {
  switch (format) {
    case ACTIVITY_FORMAT.FLEX:
      return t("Flexible Schedule");
    case ACTIVITY_FORMAT.ONGOING:
      return t("Ongoing");
    case ACTIVITY_FORMAT.ONE_TIME:
      return t("One-Time");
    case ACTIVITY_FORMAT.MULTI_DAY:
      return t("Multi-Day");
    case ACTIVITY_FORMAT.ONE_ON_ONE:
      return t("1-on-1");
    case ACTIVITY_FORMAT.CLUB:
      return t("Group");
    case ACTIVITY_FORMAT.SELF_PACED:
      return t("Self-Paced");
    default:
      throw exhaustiveCheck(format);
  }
}

function translateActivityStatus(
  status: (typeof ACTIVITY_STATUS)[keyof typeof ACTIVITY_STATUS],
  t: TFunction,
): string {
  switch (status) {
    case ACTIVITY_STATUS.LISTED:
      return t("Listed");
    case ACTIVITY_STATUS.ARCHIVED:
      return t("Archived");
    case ACTIVITY_STATUS.NOT_LISTED:
      return t("Not listed");
    case ACTIVITY_STATUS.REQUESTED_LISTING:
      return t("Requested listing");
    default:
      return exhaustiveCheck(status);
  }
}

function translateSubtopicName(
  subtopic: ActivitySubjects.Subtopic | ActivitySubjects.SubjectSubtopic,
  t: TFunction,
): string {
  switch (subtopic) {
    case "drawing":
      return t("drawing");
    case "photography":
      return t("photography");
    case "dance":
      return t("dance");
    case "theater":
      return t("theater");
    case "film":
      return t("film");
    case "sewing":
      return t("sewing");
    case "coding":
      return t("coding");
    case "video game design":
      return t("video game design");
    case "robotics":
      return t("robotics");
    case "engineering":
      return t("engineering");
    case "electronics":
      return t("electronics");
    case "internet safety":
      return t("internet safety");
    case "animation":
      return t("animation");
    case "creative writing":
      return t("creative writing");
    case "grammar & spelling":
      return t("grammar & spelling");
    case "book club":
      return t("book club");
    case "essay writing":
      return t("essay writing");
    case "poetry":
      return t("poetry");
    case "literature":
      return t("literature");
    case "book clubs":
      return t("book clubs");
    case "hygiene":
      return t("hygiene");
    case "managing emotions":
      return t("managing emotions");
    case "sex ed":
      return t("sex ed");
    case "exercise":
      return t("exercise");
    case "cooking & nutrition":
      return t("cooking & nutrition");
    case "financial skills":
      return t("financial skills");
    case "study skills":
      return t("study skills");
    case "social skills":
      return t("social skills");
    case "critical thinking":
      return t("critical thinking");
    case "algebra":
      return t("algebra");
    case "arithmetic":
      return t("arithmetic");
    case "geometry":
      return t("geometry");
    case "fractions":
      return t("fractions");
    case "calculus":
      return t("calculus");
    case "statistics & probability":
      return t("statistics & probability");
    case "instrument lessons":
      return t("instrument lessons");
    case "singing":
      return t("singing");
    case "music composition":
      return t("music composition");
    case "music studies":
      return t("music studies");
    case "chemistry":
      return t("chemistry");
    case "biology":
      return t("biology");
    case "animal studies":
      return t("animal studies");
    case "physics":
      return t("physics");
    case "astronomy":
      return t("astronomy");
    case "anatomy":
      return t("anatomy");
    case "marine biology":
      return t("marine biology");
    case "psychology":
      return t("psychology");
    case "geography":
      return t("geography");
    case "world history":
      return t("world history");
    case "us history":
      return t("us history");
    case "anthropology":
      return t("anthropology");
    case "economics":
      return t("economics");
    case "political science":
      return t("political science");
    case "spanish":
      return t("spanish");
    case "sign language":
      return t("sign language");
    case "french":
      return t("french");
    case "japanese":
      return t("japanese");
    case "latin":
      return t("latin");
    case "german":
      return t("german");
    case "chinese":
      return t("chinese");
    case "greek":
      return t("greek");
    case "italian":
      return t("italian");
    case "mandarin":
      return t("mandarin");
    case "russian":
      return t("russian");
    case "harry potter":
      return t("harry potter");
    case "dungeons and dragons":
      return t("dungeons and dragons");
    case "fortnite":
      return t("fortnite");
    case "lego":
      return t("lego");
    case "pokemon":
      return t("pokemon");
    case "minecraft":
      return t("minecraft");
    case "adhd":
      return t("ADHD");
    case "adventure":
      return t("adventure");
    case "american history":
      return t("american history");
    case "camp":
      return t("camp");
    case "cats":
      return t("cats");
    case "composers":
      return t("composers");
    case "cooking":
      return t("cooking");
    case "dog training":
      return t("dog training");
    case "dogs":
      return t("dogs");
    case "emotions":
      return t("emotions");
    case "french conversation":
      return t("french conversation");
    case "geology":
      return t("geology");
    case "grammar":
      return t("grammar");
    case "guitar":
      return t("guitar");
    case "mindfulness":
      return t("mindfulness");
    case "music theory":
      return t("music theory");
    case "numbers":
      return t("numbers");
    case "nutrition":
      return t("nutrition");
    case "piano":
      return t("piano");
    case "politics":
      return t("politics");
    case "probability":
      return t("probability");
    case "science experiments":
      return t("science experiments");
    case "spelling":
      return t("spelling");
    case "statistics":
      return t("statistics");
    case "travel":
      return t("travel");
    case "ukulele":
      return t("ukulele");
    case "unicorns":
      return t("unicorns");
    case "weather":
      return t("weather");
    case "zoology":
      return t("zoology");
    default:
      throw exhaustiveCheck(subtopic as never);
  }
}

function strictAgeRangeString(
  activity: Pick<Activity, "age_min" | "age_max" | "strictAgeRange">,
  t: TFunction,
) {
  const { age_min, age_max, strictAgeRange } = activity;
  if (
    age_min === null ||
    age_min === undefined ||
    age_max === null ||
    age_max === undefined ||
    strictAgeRange === null ||
    strictAgeRange === undefined
  ) {
    return "";
  }
  const minAge = age_min - strictAgeRange.min;
  const maxAge = age_max + strictAgeRange.max;

  if (!minAge) {
    return t("{{numMaxAge}} or younger", {
      numMaxAge: maxAge,
      numMinAge: minAge,
    });
  }

  if (!maxAge) {
    return t("{{numMinAge}} and up", {
      numMaxAge: maxAge,
      numMinAge: minAge,
    });
  }

  return t("{{numMinAge}}-{{numMaxAge}}", {
    numMaxAge: maxAge,
    numMinAge: minAge,
  });
}

/**
 * @deprecated Please port uses over to `@ui-components-website`'s `useActivityString`
 */
export function useActivityString() {
  const { t } = useTranslation("shared\\Activity");
  const sharedActivityString = sharedUseActivityString();
  return {
    translateSubtopicName(
      subtopic: ActivitySubjects.Subtopic | ActivitySubjects.SubjectSubtopic,
    ): string {
      return translateSubtopicName(subtopic, t);
    },
    evaluationOptions: [
      {
        label: t("Offers homework"),
        value: "hasHomework",
      },
      {
        label: t("Offers assessments"),
        value: "hasAssessment",
      },
      {
        label: t("Offers grades"),
        value: "hasGrades",
      },
    ],
    languageOptions: [t("English"), t("Spanish")],
    Format: [
      {
        label: t("Live online meetings"),
        value: "Live online",
      },
      {
        label: t("Self-paced"),
        value: "Self-Paced",
      },
    ],
    Delivery: Object.keys(ACTIVITY_DELIVERY).map(
      (deliveryType: DeliveryType) => {
        return {
          label: getTranslatedDeliveryType(deliveryType, t),
          value: ACTIVITY_DELIVERY[deliveryType],
        };
      },
    ),
    strictAgeRangeOptions: [
      { value: "FLEXIBLE", label: t("Flexible Age Range") },
      { value: "STRICT", label: t("Strict Age Range") },
    ],
    translateActivityClassFormat(activity: ActivityFormatType): string {
      return translateActivityClassFormat(activity, t);
    },
    translateActivityFormat(format: ACTIVITY_FORMAT): string {
      return translateActivityFormat(format, t);
    },
    status: function (
      activity: Pick<
        Activity,
        "archived_at" | "published_at" | "requested_listing_at"
      > & {
        leaderDetails?: {
          requestedListingAt?: Date;
        };
        requested_listing_at?: Date;
      },
    ): string {
      if (isArchived(activity)) {
        return translateActivityStatus(ACTIVITY_STATUS.ARCHIVED, t);
      } else if (isPublished(activity)) {
        return translateActivityStatus(ACTIVITY_STATUS.LISTED, t);
      } else if (requestedListingAt(activity)) {
        return translateActivityStatus(ACTIVITY_STATUS.REQUESTED_LISTING, t);
      } else {
        return translateActivityStatus(ACTIVITY_STATUS.NOT_LISTED, t);
      }
    },
    strictAgeRangeString(
      activity: Pick<Activity, "age_min" | "age_max" | "strictAgeRange">,
    ): string {
      return strictAgeRangeString(activity, t);
    },
    WELCOME_MESSAGE: t("WELCOME_MESSAGE", {
      strParticipationGuidelinesUrl: participationGuidelinesUrl(),
      defaultValue: `Welcome! I'm excited that you joined this class. Would you please introduce yourself to me and the other learners? Remember to keep your private information private - for example, no last names, home cities, or gaming handles! I'd also love for you to share any background or questions you have in this topic area.

As a reminder, when posting in the classroom remember to follow the Outschool guidelines to be kind, safe, and respectful: {{strParticipationGuidelinesUrl}}`,
    }),
    DEFAULT_CLUB_GUIDELINES: t(
      "DEFAULT_CLUB_GUIDELINES",
      `Welcome to our Group! We want all of our learners to feel comfortable, connected, and safe. That's why we have 3 rules we ask you to follow: Be Kind, Be Safe, and Be Respectful.

Be Kind - Help all learners feel welcome and included.
Learners from all over the world, with different backgrounds, races, ethnicities, abilities, gender identities, religions and body types, participate in our Group. Always be nice and make others, especially new learners, know that they belong and are welcome. Teasing, name calling, or bullying anybody in our community? Nope. Not happening here

Be Safe - Keep your personal information private and never ask other learners for theirs.
Personal things are personal. We want to keep you safe on Outschool, but we also want YOU to understand how to be safe when you're meeting other learners or teachers. Keep your full name, home address, gaming or social media name, email, and school name private.

Be Respectful - Treat others how you want to be treated.
Keep language clean and information you share safe. If you wouldn't say it or share with your grandma, don't do it here. Swear words, racy videos, and violent images are not allowed. Treat Outschool like a public place, and when in doubt, keep things covered.`,
    ),
    ...sharedActivityString,
  };
}
